import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAuth } from "../../utils/AuthContext";
import useCart from "../userDashboard/cart/useCart";
import useGuestCart from "../userDashboard/cart/useGuestCart";
import useLogout from "../auth/logout/useLogout";
import { getProducts, searchProducts } from "../../services/product";
import { API } from "../../config";
import useWishlist from "../userDashboard/wishlist/useWishlist";
import useGuestWishlist from "../userDashboard/wishlist/useGuestWishlist";
import useUser from "../user/useUser";
import useAddress from "../userDashboard/address/useAddress";
import LogoutConfirmationModal from "./LogoutConfirmationModal";

// import { ShoppingCart } from "lucide-react";

const Navbar = () => {
  const { user, isLoggedIn } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [isMobileSearch, setIsMobileSearch] = useState(false);
  const searchRef = useRef(null);
  const navigate = useHistory();

  const { cartItems } = useCart();
  const { guestCart } = useGuestCart();
  const { wishlistItems } = useWishlist(true);
  const { guestWishlist } = useGuestWishlist();
  const {
    logoutUserFn,
    showLogoutConfirmation,
    hideLogoutConfirmation,
    showLogoutModal,
    isLoggingOut,
  } = useLogout();
  const { userProfile } = useUser();
  const { addresses } = useAddress();

  const defaultAddress = addresses?.find(
    (address) => address.isDefault === true
  );
  // Calculate total cart items (either from guest or logged in state)
  const totalCartItems = isLoggedIn
    ? cartItems?.items?.length || 0
    : guestCart?.length || 0;

  // Calculate total wishlist items (either from guest or logged in state)
  const totalWishlistItems = isLoggedIn
    ? wishlistItems?.length || 0
    : guestWishlist?.length || 0;

  // Fetch search suggestions
  const { data: suggestions = [], isLoading: isLoadingSuggestions } = useQuery({
    queryKey: ["searchSuggestions", searchQuery],
    queryFn: () => getProducts({ search: searchQuery }),
    enabled: searchQuery?.length >= 2, // Only search when query is at least 2 characters
  });

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate.push(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    navigate.push(`/product/${product._id}`);
    setShowSuggestions(false);
    setSearchQuery("");
  };

  return (
    <nav className="bg-[#f0f0f0] md:bg-[#000] shadow-md py-3 flex items-center justify-between px-4">
      {/* LOGO */}
      <div className="flex-shrink-0 flex items-center gap-3 md:gap-5">
        <Link to="/" className="flex items-center">
          <img
            src="/zstore_logo.png"
            alt="zstore logo"
            className="h-12 object-contain w-auto"
          />
          <span className="ml-2 text-white hidden lg:inline-block">
            Z-Store
          </span>
        </Link>
        {isLoggedIn && defaultAddress && (
          <Link
            to={"/user/dashboard/addresses"}
            className="hidden md:flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={18}
              height={18}
              viewBox="0 0 24 24"
              className="stroke-white md:stroke-white"
            >
              <g
                fill="none"
                stroke={"#fff"}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              >
                <circle cx={12} cy={10} r={3}></circle>
                <path d="M12 2a8 8 0 0 0-8 8c0 1.892.402 3.13 1.5 4.5L12 22l6.5-7.5c1.098-1.37 1.5-2.608 1.5-4.5a8 8 0 0 0-8-8"></path>
              </g>
            </svg>
            <div className="">
              <p className="text-white md:text-white text-[8px] font-light">
                Deliver to
              </p>
              <p className="text-white md:text-white text-xs">
                {defaultAddress?.title}
              </p>
            </div>
          </Link>
        )}
      </div>

      {/* SEARCH */}
      <div className="flex-1 lg:mx-28 hidden md:block" ref={searchRef}>
        <form onSubmit={handleSearch} className="w-full">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="block w-full pl-5 pr-12 py-2 border border-white text-white rounded-xl leading-5 bg-transparent placeholder-gray-300 focus:placeholder-gray-400 outline-none text-[8px] sm:text-xs"
              placeholder="Search products..."
            />
            <button
              type="submit"
              className="absolute flex justify-center mx-auto text-center cursor-pointer inset-y-0 right-0 rounded-r-xl bg-white px-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={20}
                viewBox="0 0 24 24"
                className="text-center mx-auto mt-[6px]"
              >
                <path
                  fill="none"
                  stroke="#000"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeMiterlimit={10}
                  strokeWidth={1.5}
                  d="m21 21l-4-4m2-6a8 8 0 1 1-16 0a8 8 0 0 1 16 0"
                ></path>
              </svg>
            </button>

            {/* Search Suggestions */}
            {showSuggestions && searchQuery?.length >= 2 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {isLoadingSuggestions ? (
                  <div className="p-4 text-gray-500">Loading...</div>
                ) : suggestions.data?.length > 0 ? (
                  <div className="py-2">
                    {suggestions?.data?.map((product) => (
                      <div
                        key={product._id}
                        onClick={() => handleSuggestionClick(product)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                      >
                        <img
                          src={`${API}/products/photo/${product._id}`}
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div>
                          <p className="text-gray-900 font-medium">
                            {product.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {product.price} EGP
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-gray-500">No products found</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* USER LINKS */}
      <div className="hidden md:flex items-center gap-8 flex-shrink-0">
        {/* PROFILE */}
        {isLoggedIn && (
          <Link
            title="profile"
            to={
              isLoggedIn
                ? user?.role === 1
                  ? "/admin/dashboard"
                  : "/user/dashboard"
                : "/signin"
            }
            className="flex flex-col items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={26}
              height={26}
              viewBox="0 0 24 24"
              className="cursor-pointer"
              title="profile"
            >
              <g
                fill="#fff"
                fillRule="evenodd"
                clipRule="evenodd"
                strokeWidth={0.5}
                stroke="#fff"
              >
                <path d="M16 9a4 4 0 1 1-8 0a4 4 0 0 1 8 0m-2 0a2 2 0 1 1-4 0a2 2 0 0 1 4 0"></path>
                <path d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11s11-4.925 11-11S18.075 1 12 1M3 12c0 2.09.713 4.014 1.908 5.542A8.99 8.99 0 0 1 12.065 14a8.98 8.98 0 0 1 7.092 3.458A9 9 0 1 0 3 12m9 9a8.96 8.96 0 0 1-5.672-2.012A6.99 6.99 0 0 1 12.065 16a6.99 6.99 0 0 1 5.689 2.92A8.96 8.96 0 0 1 12 21"></path>
              </g>
            </svg>
            <span className="text-white text-xs mt-1">Profile</span>
          </Link>
        )}
        {!isLoggedIn && (
          <Link
            title="login"
            to={"/signin"}
            className="flex flex-col items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={20}
              height={20}
              viewBox="0 0 20 20"
            >
              <path
                fill="#fff"
                d="M9.76 0C15.417 0 20 4.477 20 10S15.416 20 9.76 20c-3.191 0-6.142-1.437-8.07-3.846a.644.644 0 0 1 .115-.918a.68.68 0 0 1 .94.113a8.96 8.96 0 0 0 7.016 3.343c4.915 0 8.9-3.892 8.9-8.692s-3.985-8.692-8.9-8.692a8.96 8.96 0 0 0-6.944 3.255a.68.68 0 0 1-.942.101a.644.644 0 0 1-.103-.92C3.703 1.394 6.615 0 9.761 0m.545 6.862l2.707 2.707c.262.262.267.68.011.936L10.38 13.15a.66.66 0 0 1-.937-.011a.66.66 0 0 1-.01-.937l1.547-1.548l-10.31.001A.66.66 0 0 1 0 10c0-.361.3-.654.67-.654h10.268L9.38 7.787a.66.66 0 0 1-.01-.937a.66.66 0 0 1 .935.011"
                strokeWidth={0.5}
                stroke="#fff"
              ></path>
            </svg>
            <span className="text-white text-xs mt-1">Login</span>
          </Link>
        )}
        <span className="bg-white w-[1px] h-6"></span>

        {/* HEART */}
        {user?.role === 1 && isLoggedIn ? (
          <Link
            to={"/admin/users"}
            className="relative flex flex-col items-center"
            title="users"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 256 256"
            >
              <path
                fill="#fff"
                d="M244.8 150.4a8 8 0 0 1-11.2-1.6A51.6 51.6 0 0 0 192 128a8 8 0 0 1-7.37-4.89a8 8 0 0 1 0-6.22A8 8 0 0 1 192 112a24 24 0 1 0-23.24-30a8 8 0 1 1-15.5-4A40 40 0 1 1 219 117.51a67.94 67.94 0 0 1 27.43 21.68a8 8 0 0 1-1.63 11.21M190.92 212a8 8 0 1 1-13.84 8a57 57 0 0 0-98.16 0a8 8 0 1 1-13.84-8a72.06 72.06 0 0 1 33.74-29.92a48 48 0 1 1 58.36 0A72.06 72.06 0 0 1 190.92 212M128 176a32 32 0 1 0-32-32a32 32 0 0 0 32 32m-56-56a8 8 0 0 0-8-8a24 24 0 1 1 23.24-30a8 8 0 1 0 15.5-4A40 40 0 1 0 37 117.51a67.94 67.94 0 0 0-27.4 21.68a8 8 0 1 0 12.8 9.61A51.6 51.6 0 0 1 64 128a8 8 0 0 0 8-8"
                strokeWidth={6.5}
                stroke="#fff"
              ></path>
            </svg>

            <span className="text-white text-xs mt-1">Users</span>
          </Link>
        ) : (
          <Link
            to={`${!isLoggedIn ? "/wishlist" : "/user/dashboard/wishlist"}`}
            className="relative flex flex-col items-center"
            title="wishlist"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              className="cursor-pointer"
              title="wishlist"
            >
              <path
                fill="#fff"
                d="M12.001 4.529a6 6 0 0 1 8.242.228a6 6 0 0 1 .236 8.236l-8.48 8.492l-8.478-8.492a6 6 0 0 1 8.48-8.464m6.826 1.641a4 4 0 0 0-5.49-.153l-1.335 1.198l-1.336-1.197a4 4 0 0 0-5.686 5.605L12 18.654l7.02-7.03a4 4 0 0 0-.193-5.454"
                strokeWidth={0.5}
                stroke="#fff"
              ></path>
            </svg>
            {totalWishlistItems > 0 && (
              <span className="bg-black rounded-full text-white h-4 w-4 inline-block text-center text-xs absolute -top-3 -right-2">
                {totalWishlistItems}
              </span>
            )}
            <span className="text-white text-xs mt-1">Wishlist</span>
          </Link>
        )}
        <span className="bg-white w-[1px] h-6"></span>
        {/* CART */}
        {user?.role === 1 && isLoggedIn ? (
          <Link
            to={"/admin/products"}
            className="relative flex flex-col items-center"
            title="cart"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 2048 2048"
            >
              <path
                fill="#fff"
                d="m1344 2l704 352v785l-128-64V497l-512 256v258l-128 64V753L768 497v227l-128-64V354zm0 640l177-89l-463-265l-211 106zm315-157l182-91l-497-249l-149 75zm-507 654l-128 64v-1l-384 192v455l384-193v144l-448 224L0 1735v-676l576-288l576 288zm-640 710v-455l-384-192v454zm64-566l369-184l-369-185l-369 185zm576-1l448-224l448 224v527l-448 224l-448-224zm384 576v-305l-256-128v305zm384-128v-305l-256 128v305zm-320-288l241-121l-241-120l-241 120z"
                strokeWidth={51}
                stroke="#fff"
              ></path>
            </svg>

            <span className="text-white text-xs mt-1">Products</span>
          </Link>
        ) : (
          <Link
            to={`${isLoggedIn ? "/user/dashboard/cart" : "/cart"}`}
            className="relative flex flex-col items-center"
            title="cart"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              className="cursor-pointer"
              title="cart"
            >
              <path
                fill="#fff"
                d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2s-.9-2-2-2M1 2v2h2l3.6 7.59l-1.35 2.45c-.16.28-.25.61-.25.96c0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12l.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48c0-.55-.45-1-1-1H5.21l-.94-2M17 18c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2s2-.9 2-2s-.9-2-2-2"
              ></path>
            </svg>
            {totalCartItems > 0 && (
              <span className="bg-black rounded-full text-white h-4 w-4 inline-block text-center text-xs absolute -top-3 -right-2">
                {totalCartItems}
              </span>
            )}
            <span className="text-white text-xs mt-1">Cart</span>
          </Link>
        )}
        {isLoggedIn && (
          <button
            title="logout"
            onClick={showLogoutConfirmation}
            className="flex flex-col items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 20 20"
            >
              <path
                fill="#fff"
                d="M10.24 0c3.145 0 6.057 1.395 7.988 3.744a.644.644 0 0 1-.103.92a.68.68 0 0 1-.942-.1a8.96 8.96 0 0 0-6.944-3.256c-4.915 0-8.9 3.892-8.9 8.692s3.985 8.692 8.9 8.692a8.96 8.96 0 0 0 7.016-3.343a.68.68 0 0 1 .94-.113a.644.644 0 0 1 .115.918C16.382 18.564 13.431 20 10.24 20C4.583 20 0 15.523 0 10S4.584 0 10.24 0m6.858 7.16l2.706 2.707c.262.261.267.68.012.936l-2.644 2.643a.66.66 0 0 1-.936-.01a.66.66 0 0 1-.011-.937l1.547-1.547H7.462a.66.66 0 0 1-.67-.654c0-.362.3-.655.67-.655h10.269l-1.558-1.558a.66.66 0 0 1-.011-.936a.66.66 0 0 1 .936.011"
                strokeWidth={0.5}
                stroke="#fff"
              ></path>
            </svg>
            <span className="text-white text-xs mt-1">Logout</span>
          </button>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        open={showLogoutModal}
        onClose={hideLogoutConfirmation}
        onConfirm={logoutUserFn}
        loading={isLoggingOut}
        error={null}
      />

      {/* RESPONSIVE SEARCH */}
      <button
        type="button"
        className="flex justify-end cursor-pointer md:hidden"
        onClick={() => setIsMobileSearch(true)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={50}
          height={50}
          viewBox="0 0 24 24"
          className="text-center mx-auto mt-[8px]"
        >
          <path
            fill="none"
            stroke="#000"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit={10}
            strokeWidth={1.5}
            d="m21 21l-4-4m2-6a8 8 0 1 1-16 0a8 8 0 0 1 16 0"
          ></path>
        </svg>
      </button>

      {/* Mobile Search Overlay */}
      {isMobileSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
          <div className="bg-white p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Search Products</h2>
              <button
                onClick={() => {
                  setIsMobileSearch(false);
                  setShowSuggestions(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSearch} className="w-full" ref={searchRef}>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="block w-full pl-5 pr-12 py-3 border border-gray-300 text-gray-900 rounded-xl leading-5 bg-white placeholder-gray-400 focus:placeholder-gray-500 outline-none"
                  placeholder="Search products..."
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute flex justify-center mx-auto text-center cursor-pointer inset-y-0 right-0 rounded-r-xl bg-[#DA0E1A] px-4"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    className="text-center mx-auto mt-[8px]"
                  >
                    <path
                      fill="none"
                      stroke="#fff"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeMiterlimit={10}
                      strokeWidth={1.5}
                      d="m21 21l-4-4m2-6a8 8 0 1 1-16 0a8 8 0 0 1 16 0"
                    ></path>
                  </svg>
                </button>

                {/* Search Suggestions */}
                {showSuggestions && searchQuery?.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg max-h-96 overflow-y-auto">
                    {isLoadingSuggestions ? (
                      <div className="p-4 text-gray-500">Loading...</div>
                    ) : suggestions.data?.length > 0 ? (
                      <div className="py-2">
                        {suggestions?.data?.map((product) => (
                          <div
                            key={product._id}
                            onClick={() => handleSuggestionClick(product)}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                          >
                            <img
                              src={`${API}/products/photo/${product._id}`}
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                            <div>
                              <p className="text-gray-900 font-medium">
                                {product.name}
                              </p>
                              <p className="text-gray-500 text-sm">
                                {product.price} EGP
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-gray-500">No products found</div>
                    )}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-40">
        <div className="flex items-center justify-around py-2">
          <Link
            to="/"
            className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span className="text-xs mt-1">Home</span>
          </Link>

          <Link
            to={`${!isLoggedIn ? "/cart" : "/user/dashboard/cart"}`}
            className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
          >
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="8" cy="21" r="1"></circle>
                <circle cx="19" cy="21" r="1"></circle>
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
              </svg>
              {totalCartItems === 0 ||
                (totalCartItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#DA0E1A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {totalCartItems}
                  </span>
                ))}
            </div>
            <span className="text-xs mt-1">Cart</span>
          </Link>

          <Link
            to={`${!isLoggedIn ? "/wishlist" : "/user/dashboard/wishlist"}`}
            className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
          >
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
              {totalWishlistItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#DA0E1A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {totalWishlistItems}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">Wishlist</span>
          </Link>

          <Link
            to="/order"
            className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 1024 1024"
            >
              <path
                fill="currentColor"
                d="M864 158.704H672.815V97.328c0-52.944-43.056-96-96-96H449.183c-52.944 0-96 43.056-96 96v61.376H159.999c-35.344 0-64 28.656-64 64v735.968c0 35.344 28.656 64 64 64h704c35.344 0 64-28.656 64-64V222.704c0-35.344-28.656-64-64-64zM417.184 97.328c0-17.664 14.336-32 32-32h127.632c17.664 0 32 14.336 32 32v61.376H417.184zM864 958.672H160V222.704h193.184v65.84s-.848 31.967 31.809 31.967c36 0 32.192-31.967 32.192-31.967v-65.84h191.632v65.84s-2.128 32.128 31.872 32.128c32 0 32.128-32.128 32.128-32.128v-65.84h191.184z"
                strokeWidth={25.5}
                stroke="currentColor"
              ></path>
            </svg>

            <span className="text-xs mt-1">Order</span>
          </Link>

          {isLoggedIn ? (
            <Link
              to={
                isLoggedIn
                  ? user?.role === 1
                    ? "/admin/dashboard"
                    : "/user/dashboard"
                  : "/signin"
              }
              className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span className="text-xs mt-1">Profile</span>
            </Link>
          ) : (
            <Link
              to="/signin"
              className="flex flex-col items-center text-gray-600 hover:text-[#DA0E1A]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span className="text-xs mt-1">Sign in</span>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
